import { PaperGradientBG } from "../kits";
import rough from "roughjs/bundled/rough.esm.js";
import { useEffect, useLayoutEffect, useState, useRef } from "react";
import TerminalDetailOverlay from "./TerminalDetailOverlay";
import { COMPANY_DETAIL_CONFIG, toCompanyKey } from "./CompanyDetailConfig";
import { useTheme } from "../context/ThemeContext";

function RoughCircle({ size = 56, color = "#AC8E66", strokeWidth = 1.5, roughness = 1.5, className = "" }) {
  const svgRef = useRef(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useLayoutEffect(() => {
    if (!isClient || !svgRef.current) return;
    svgRef.current.innerHTML = "";
    const rc = rough.svg(svgRef.current);
    const padding = strokeWidth;
    const node = rc.circle(size / 2, size / 2, size - padding * 2, {
      roughness,
      stroke: color,
      strokeWidth,
      fill: "none",
    });
    svgRef.current.appendChild(node);
  }, [isClient, size, color, strokeWidth, roughness]);

  if (!isClient) return null;

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      className={`absolute inset-0 pointer-events-none ${className}`}
    />
  );
}

export default function CompanyTerminal({
  companies = [],
  onCompanyClick,
  enableDetailOverlay = false,
  withBackground = true,
}) {
  const { isDark } = useTheme();

  const t = isDark
    ? {
        bgVariant:  { stops: ["#171717", "#171717"], angle: 180 },
        titleBar:       "bg-[#1a1a1a]",
        cardBg:         "bg-[#0F0F0F]",
        titleLabel:     "text-[#AC8E66]",
        promptText:     "text-[#d0cbb8]/80",
        iconBg:         "bg-[#1c1c1c]",
        hintText:       "text-green-400/70",
        detailsLink:    "text-[#AC8E66]/70 hover:text-[#AC8E66]",
        terminalBorder: "border-[#AC8E66]/30",
      }
    : {
        bgVariant:      { stops: ["#d0cbb8", "#d0cbb8"], angle: 180 },
        titleBar:       "bg-[#b8b2a0]",
        cardBg:         "bg-[#c8c2b0]",
        titleLabel:     "text-[#1a1a1a]/60",
        promptText:     "text-[#1a1a1a]/80",
        iconBg:         "bg-[#b0aa98]",
        hintText:       "text-green-700/70",
        detailsLink:    "text-[#1a1a1a]/60 hover:text-[#1a1a1a]",
        terminalBorder: "border-[#1a1a1a]/50",
      };

  const [activeCompanyKey, setActiveCompanyKey] = useState(null);
  const orderedCompanyKeys = companies
    .map((company) => toCompanyKey(company.name))
    .filter((key, index, arr) => arr.indexOf(key) === index && Boolean(COMPANY_DETAIL_CONFIG[key]));
  const activeCompanyIndex = activeCompanyKey ? orderedCompanyKeys.indexOf(activeCompanyKey) : -1;
  const activeCompanyDetail = activeCompanyKey ? COMPANY_DETAIL_CONFIG[activeCompanyKey] : null;

  const handleCompanyClick = (companyName) => {
    if (enableDetailOverlay) {
      const key = toCompanyKey(companyName);
      if (COMPANY_DETAIL_CONFIG[key]) {
        setActiveCompanyKey(key);
        return;
      }
    }
    onCompanyClick?.();
  };
  const handlePrev = () => {
    if (activeCompanyIndex <= 0) return;
    setActiveCompanyKey(orderedCompanyKeys[activeCompanyIndex - 1]);
  };
  const handleNext = () => {
    if (activeCompanyIndex < 0 || activeCompanyIndex >= orderedCompanyKeys.length - 1) return;
    setActiveCompanyKey(orderedCompanyKeys[activeCompanyIndex + 1]);
  };

  const overlay = (
    <TerminalDetailOverlay
      isOpen={Boolean(activeCompanyDetail)}
      onClose={() => setActiveCompanyKey(null)}
      title={activeCompanyDetail?.title || "Company"}
      subtitle={activeCompanyDetail?.subtitle || "ls company/"}
      lines={activeCompanyDetail?.lines || []}
      storageKey={activeCompanyDetail?.storageKey}
      ctaLabel="saghallo"
      ctaMode="inlineContact"
      onPrev={handlePrev}
      onNext={handleNext}
      hasPrev={activeCompanyIndex > 0}
      hasNext={activeCompanyIndex >= 0 && activeCompanyIndex < orderedCompanyKeys.length - 1}
    />
  );

  const terminal = (
    <div className={`rounded-lg border ${t.terminalBorder} overflow-hidden shadow-xl shadow-black/10 font-mono`}>
      <div className={`flex items-center gap-2 px-4 py-2.5 ${t.titleBar} border-b border-[#AC8E66]/10`}>
        <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
        <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
        <span className="w-3 h-3 rounded-full bg-[#28c840]" />
        <span className={`ml-3 text-xs ${t.titleLabel} select-none`}>vertrauen</span>
      </div>
      <div className={`px-4 sm:px-6 py-5 ${t.cardBg}`}>
        <p className={`text-sm ${t.promptText} mb-4`}>
          <span className="text-[#AC8E66]">$</span> trust --verified
        </p>
        <div className="flex flex-wrap justify-center gap-4 sm:gap-5">
          {companies.map((company) => (
            <div
              key={company.name}
              onClick={() => handleCompanyClick(company.name)}
              className={`relative w-[60px] h-[60px] sm:w-[68px] sm:h-[68px] flex items-center justify-center ${t.iconBg} rounded-full group cursor-pointer`}
              title={company.name}
            >
              <RoughCircle size={60} color="#AC8E66" strokeWidth={1} roughness={0.8} className="sm:hidden" />
              <RoughCircle size={68} color="#AC8E66" strokeWidth={1} roughness={0.8} className="hidden sm:block" />
              <img
                src={company.image}
                alt={company.name}
                className={`object-contain opacity-60 group-hover:opacity-100 transition-opacity ${
                  company.dark ? (isDark ? "brightness-0 invert" : "brightness-0") : ""
                } ${company.large ? "w-10 h-10 sm:w-12 sm:h-12" : "w-6 h-6 sm:w-7 sm:h-7"}`}
              />
            </div>
          ))}
        </div>
        <p className={`text-xs ${t.hintText} mt-5 pt-4 border-t border-[#AC8E66]/10`}>
          <span className="text-[#AC8E66]/80">&gt;</span> {companies.length} partner verifiziert.{" "}
          <span
            onClick={() => {
              if (enableDetailOverlay && companies[0]?.name) {
                handleCompanyClick(companies[0].name);
                return;
              }
              onCompanyClick?.();
            }}
            className={`${t.detailsLink} cursor-pointer underline underline-offset-2 transition-colors`}
          >
            details anzeigen →
          </span>
        </p>
      </div>
    </div>
  );

  if (!withBackground) {
    return <>{terminal}{overlay}</>;
  }

  return (
    <PaperGradientBG variant={t.bgVariant}>
      <section className="py-12 md:py-20">
        <div className="max-w-3xl mx-auto px-6">{terminal}</div>
      </section>
      {overlay}
    </PaperGradientBG>
  );
}
